import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslations } from 'next-intl';

import { FooterLinkTextKey, FooterMenuItem as FooterMenuItemType, FooterMenuTitleKey } from '@/config/footer.config';
import { Link } from '@/i18n/navigation';
import colors from '@/styles/themes/colors';

import styles from './FooterMenuItem.module.scss';

interface FooterMenuItemProps extends FooterMenuItemType {
  isAddress?: boolean;
}

const isExternalLink = (href: string) =>
  href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('http');

const FooterMenuItem = ({ title, links, isAddress = false, addressUrl }: FooterMenuItemProps) => {
  const t = useTranslations('common');

  const renderBadge = () => (
    <Typography
      variant="body3"
      component="span"
      color={colors.blue500}
      textTransform="uppercase"
      className={styles.aiBadge}
      aria-hidden
    >
      {t('footer.ai.badge')}
    </Typography>
  );

  const renderInner = (displayText: string, hasAiBadge?: boolean) => (
    <>
      {hasAiBadge ? renderBadge() : null}
      <Typography variant="body1" component="span" className={styles.linkText}>
        {displayText}
      </Typography>
    </>
  );

  const renderLink = (href: string, displayText: string, hasAiBadge?: boolean) => {
    if (href === '#') {
      return <Box className={styles.text}>{renderInner(displayText, hasAiBadge)}</Box>;
    }

    if (isExternalLink(href)) {
      return (
        <a href={href} target="_blank" rel="nofollow noopener noreferrer" className={styles.link}>
          {renderInner(displayText, hasAiBadge)}
        </a>
      );
    }

    return (
      <Link href={href as '/'} className={styles.link}>
        {renderInner(displayText, hasAiBadge)}
      </Link>
    );
  };

  const content = (
    <List disablePadding classes={{ root: styles.root }} className={styles.list}>
      {links.map(({ text, href, isTranslationKey, hasAiBadge }) => {
        const displayText = isTranslationKey ? t(text as FooterLinkTextKey) : text;

        return (
          <ListItem key={text} disablePadding>
            {renderLink(href, displayText, hasAiBadge)}
          </ListItem>
        );
      })}
    </List>
  );

  const renderContent = () => {
    if (isAddress && addressUrl) {
      return (
        <Box component="address" className={styles.address}>
          <a
            href={addressUrl}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className={styles.addressLink}
            aria-label="View office location on Google Maps"
          >
            {content}
          </a>
        </Box>
      );
    }

    if (isAddress) {
      return (
        <Box component="address" className={styles.address}>
          {content}
        </Box>
      );
    }

    return content;
  };

  return (
    <Stack direction="column">
      <Typography variant="subtitle1" fontWeight={500} component="p" className={styles.title}>
        {t(title as FooterMenuTitleKey)}
      </Typography>
      {renderContent()}
    </Stack>
  );
};

export default FooterMenuItem;
